import { ConfigApi } from '@liks/backend-svc-api/esm/apis/ConfigApi';
import type { Locale } from '../i18n/resources';
import {
  serializeUserConfigEntry,
  userConfigEntriesToPartial,
} from '../userConfig/userConfigModel';
import {
  USER_CONFIG_KEYS,
  type UserConfig,
  type UserConfigKey,
} from '../types/userConfig';
import { backendApiConfiguration } from './backendApi';
import { userService } from './UserService';

/**
 * Backend user-config rows use optimistic locking (`VERSION`). Concurrent
 * create-or-replace calls for the same key fail with "expected row count 1 but
 * was 0". Keep all writes on one chain so login seeds / last-seen / settings
 * cannot overlap.
 */
let userConfigWriteChain: Promise<void> = Promise.resolve();

function enqueueUserConfigWrite(task: () => Promise<void>): Promise<void> {
  const next = userConfigWriteChain.then(task, task);
  // Keep the chain alive after failures so later writes still run.
  userConfigWriteChain = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}

class UserConfigService {
  private readonly configApi = new ConfigApi(backendApiConfiguration);

  async getConfig(): Promise<UserConfig> {
    const entries =
      await this.configApi.deLiksConfigBoundaryConfigResourceListUserConfig();
    return userConfigEntriesToPartial(entries);
  }

  async updateConfig(updates: UserConfig): Promise<void> {
    return enqueueUserConfigWrite(() => this.writeConfigUpdates(updates));
  }

  private async writeConfigUpdates(updates: UserConfig): Promise<void> {
    // Sequential per call — parallel key writes can still race on some backends.
    for (const key of USER_CONFIG_KEYS) {
      const value = updates[key];
      if (value === undefined) {
        continue;
      }

      await this.writeConfigKey(key, value);

      if (key === 'locale') {
        await userService.updateCurrentUserLanguage(value as Locale);
      }
    }
  }

  private async writeConfigKey(
    key: UserConfigKey,
    value: NonNullable<UserConfig[UserConfigKey]>,
  ): Promise<void> {
    await this.configApi.deLiksConfigBoundaryConfigResourceUpdateUserConfig({
      key,
      body: serializeUserConfigEntry(key, value),
    });
  }
}

export const userConfigService = new UserConfigService();
